/**
 * `entities/brief` mutation hooks.
 *
 * `useCreateBriefMutation` is a thin TanStack `useMutation` wrapping
 * `ProjectBriefsService.create` from the workspace SDK. Mirrors
 * `briefsQueryOptions` (in `./queries`) for consumption style: bind once at
 * the call site with `{ projectId }`, then call `mutate`/`mutateAsync` with
 * the request body and an optional `AbortSignal`.
 *
 * Design notes:
 *   - On success, invalidates `BRIEFS_QUERY_KEYS.lists(projectId)` — the
 *     broad project-scoped key that matches every paginated/searched/sorted
 *     list under that project. This guarantees a freshly-created brief
 *     surfaces on the very next render of any list query, regardless of the
 *     active page/filter.
 *   - The SDK envelope mirrors `queries.ts`: `workspaceClient.setConfig({
 *     throwOnError: true })` is set globally in `lib/api/workspace-client.ts`,
 *     so HTTP errors reject the underlying promise — no per-call
 *     `throwOnError` flag needed.
 *   - The optional `signal` param threads through to the SDK so callers can
 *     compose this mutation under a parent `AbortController` (e.g. the brief-
 *     generation flow's single shared controller covering uploads + stream +
 *     persist). The mutation never injects a default signal — when the
 *     caller omits it, the SDK call's `signal` is `undefined`.
 */
import {
  ProjectBriefsService,
  type BriefResponseDto,
  type CreateBriefDto,
  type CreateBriefSectionDto,
  type CreateBriefSourceDto,
  type UpdateBriefDto,
} from '@inference.engine/workspace-api-client/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { workspaceHttp } from '@/lib/api/http-client';
import type { ProjectBriefsGroup } from './queries';
import { BRIEFS_QUERY_KEYS } from './query-keys';
import { briefActivityQueryOptions } from './activity-queries';
import { i18n } from '@/shared/i18n';

export { useSubmitBrief, type UseSubmitBriefMutationOptions } from './useSubmitBrief';

/**
 * Trackers automations run in a background worker AFTER the brief update's HTTP
 * response returns, so the optimistic cache write shows the pre-automation
 * state. A beat later — twice, to catch a chained rule — we re-read just the
 * edited brief (single GET) and patch it into the `allByWorkspace` cache in
 * place. We deliberately avoid invalidating the whole list: that forces a
 * list-wide refetch and the board visibly blanks. Patching one brief means only
 * its cells update, matching the monday.com feel. Gated via
 * `refetchForAutomations` so briefs boards with no enabled rules pay nothing.
 */
const AUTOMATION_REFETCH_DELAYS_MS = [700, 2000] as const;

function scheduleAutomationRefresh(
  refresh: () => void | Promise<void>,
  enabled: boolean | undefined,
): void {
  if (!enabled) return;
  for (const ms of AUTOMATION_REFETCH_DELAYS_MS) {
    setTimeout(() => void refresh(), ms);
  }
}

/**
 * Re-reads a single brief and patches it into the workspace briefs cache in
 * place — used to surface a worker's automation write without a list refetch.
 *
 * Like {@link patchBriefValueInGroups}, this matches by the globally-unique
 * brief id across ALL groups and never gates on `group.projectId`: on
 * duplicate/aliased-project boards the write/fetch projectId can differ from
 * the projectId a group was cached under, so a projectId gate would skip every
 * group and drop the automation-result patch silently (cell stale until
 * reload) — the same mismatch fixed on the optimistic path.
 */
async function patchBriefFromServer(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  platformProjectId: string,
  briefId: string,
): Promise<void> {
  const res = await ProjectBriefsService.getById({
    client: workspaceHttp,
    path: { platformProjectId, briefId },
  });
  const fresh = res.data;
  if (!fresh) return;
  queryClient.setQueryData<ProjectBriefsGroup[]>(
    BRIEFS_QUERY_KEYS.allByWorkspace(workspaceId),
    (groups) =>
      groups?.map((g) =>
        g.briefs.some((b) => b.id === briefId)
          ? { ...g, briefs: g.briefs.map((b) => (b.id === briefId ? fresh : b)) }
          : g,
      ),
  );
}

/**
 * {@link patchBriefFromServer} for a caller that knows only the brief: the
 * project comes from the cache group holding it, as the board fetched it. A brief the cache does not
 * hold is not on any open board, so there is nothing to patch.
 */
export async function refreshWorkspaceBrief(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  briefId: string,
): Promise<void> {
  const groups = queryClient.getQueryData<ProjectBriefsGroup[]>(
    BRIEFS_QUERY_KEYS.allByWorkspace(workspaceId),
  );
  const group = groups?.find((g) => g.briefs.some((b) => b.id === briefId));
  if (group) await patchBriefFromServer(queryClient, workspaceId, group.platformProjectId, briefId);
}

/** Inputs accepted by {@link useCreateBriefMutation}. */
export interface UseCreateBriefMutationOptions {
  projectId: string;
}

/** Variables accepted by the mutation function returned by the hook. */
export interface CreateBriefMutationVariables {
  body: CreateBriefDto;
  signal?: AbortSignal;
}

export const useCreateBriefMutation = ({ projectId }: UseCreateBriefMutationOptions) => {
  const queryClient = useQueryClient();

  return useMutation<BriefResponseDto, Error, CreateBriefMutationVariables>({
    mutationFn: async ({ body, signal }) => {
      const response = await ProjectBriefsService.create({
        client: workspaceHttp,
        path: { platformProjectId: projectId },
        body,
        signal,
      });

      if (!response.data) {
        throw new Error('Failed to create brief');
      }

      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: BRIEFS_QUERY_KEYS.lists(projectId),
      });
    },
  });
};

/**
 * Options consumed by {@link useUpdateBrief}.
 *
 * Identity (`projectId`, `briefId`) is bound at hook construction; per-call
 * data lives in {@link UpdateBriefMutationVariables}. Consumer callbacks
 * (`onSuccess`, `onError`) own toast + navigation — the hook itself is a
 * pure cache adapter (no toast service, no navigation hook).
 */
export interface UseUpdateBriefMutationOptions {
  projectId: string;
  briefId: string;
  /**
   * Called AFTER the hook has rebased the detail cache and invalidated the
   * list cache. The fresh brief is forwarded as-is. Use this to call
   * `form.rebaseline(fresh)` or trigger any post-save UI updates.
   */
  onSuccess?: (fresh: BriefResponseDto) => void;
  /**
   * Called when the SDK rejects. The hook does NOT write to the cache on
   * error. Use this to surface an error toast or set local error state.
   */
  onError?: (error: Error) => void;
}

/**
 * Variables passed per-call to the mutation function.
 *
 * `body` accepts any subset of `UpdateBriefDto` — the BE PATCH semantics
 * tolerate partial payloads. Auto-save callers should send only
 * `{ title?, values? }` (sections trigger a BE replace-all that re-issues
 * section IDs and breaks TipTap cursor preservation); manual Save MAY
 * include `sections` because the user explicitly accepted the round trip.
 */
export interface UpdateBriefMutationVariables {
  body: UpdateBriefDto;
  signal?: AbortSignal;
}

/**
 * `useUpdateBrief` — TanStack mutation wrapping `ProjectBriefsService.update`.
 *
 * Cache strategy:
 *   1. On success, write the fresh brief DIRECTLY to the detail cache via
 *      `queryClient.setQueryData(BRIEFS_QUERY_KEYS.detail(projectId, briefId), fresh)`.
 *      This is a silent rebase — no refetch, no flicker mid-edit.
 *   2. Invalidate the project-scoped lists key so any open list view
 *      refetches its paginated/sorted/searched variants.
 *   3. NEVER invalidate the detail key — that would queue a refetch and
 *      reset whatever the user is currently typing. The freshly-PATCHed
 *      response IS the source of truth; trust it.
 *
 * Side-effect policy:
 *   - No toast call — consumer's `onError` owns user-facing error surfacing.
 *   - No navigation hook — consumer's `onSuccess` owns route changes.
 *   - No direct mount-time React effect (project no-use-effect rule — the
 *     mutation lifecycle callbacks are events, not effects).
 */
export const useUpdateBrief = ({
  projectId,
  briefId,
  onSuccess,
  onError,
}: UseUpdateBriefMutationOptions) => {
  const queryClient = useQueryClient();

  return useMutation<BriefResponseDto, Error, UpdateBriefMutationVariables>({
    mutationFn: async ({ body, signal }) => {
      const response = await ProjectBriefsService.update({
        client: workspaceHttp,
        path: { platformProjectId: projectId, briefId },
        body,
        signal,
      });

      if (!response.data) {
        throw new Error('Failed to update brief');
      }

      return response.data;
    },
    onSuccess: (fresh) => {
      // Direct cache rebase — see the cache strategy above.
      queryClient.setQueryData(BRIEFS_QUERY_KEYS.detail(projectId, briefId), fresh);
      // Lists may show derived data (title, updatedAt, status) — refresh them.
      void queryClient.invalidateQueries({
        queryKey: BRIEFS_QUERY_KEYS.lists(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: briefActivityQueryOptions({ projectId, briefId }).queryKey,
      });
      // Forward to consumer AFTER cache writes so the consumer sees a
      // rebased cache when it reads.
      onSuccess?.(fresh);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

/** Identity inputs shared by the delete + duplicate mutations. */
export interface UseBriefMutationIdentity {
  projectId: string;
  briefId: string;
}

/**
 * `useDeleteBrief` — TanStack mutation wrapping `ProjectBriefsService.delete`.
 *
 * Unlike the pure `useUpdateBrief` adapter, this hook owns its own success/
 * error toast (mirroring `useDeleteBriefTemplate`) because every delete entry
 * point wants identical feedback — there is no per-call variation worth
 * delegating to the consumer. On success it invalidates the project-scoped
 * lists key so the deleted row drops from any open list view.
 */
export const useDeleteBrief = ({ projectId, briefId }: UseBriefMutationIdentity) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await ProjectBriefsService.delete({
        client: workspaceHttp,
        path: { platformProjectId: projectId, briefId },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: BRIEFS_QUERY_KEYS.lists(projectId),
      });
      enqueueSnackbar(i18n.t('common:brief.mutations.briefDeleted'), { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar(i18n.t('common:brief.mutations.failedToDeleteBrief'), { variant: 'error' });
    },
  });
};

/**
 * Options consumed by {@link useDuplicateBrief}. Identity is required; the
 * optional callbacks let a consumer own the success/error feedback (e.g. the
 * detail page wants a toast with an "Open copy" link). When omitted, the hook
 * falls back to its own default toasts so existing call sites (the table row
 * menu) keep working unchanged.
 */
export interface UseDuplicateBriefOptions extends UseBriefMutationIdentity {
  /** Called with the freshly created copy AFTER the lists cache is invalidated. */
  onSuccess?: (created: BriefResponseDto) => void;
  onError?: (error: Error) => void;
}

/**
 * `useDuplicateBrief` — clones an existing brief.
 *
 * The duplicate is atomic from the user's perspective: fetch the source via
 * `getById`, then create the copy in a single `POST` carrying the full payload
 * (title + " Copy", briefType, fields, values, summary, sections, sources).
 * `CreateBriefDto` already accepts every field we copy, so this avoids the
 * orphaned-empty-brief window a two-step create-then-update would leave behind.
 *
 * `referenceTemplateId` is intentionally NOT copied: the response models it as
 * an opaque object while `CreateBriefDto` expects a plain string, and it is
 * reference-only (no FK), so dropping it keeps the copy clean.
 */
export const useDuplicateBrief = ({
  projectId,
  briefId,
  onSuccess,
  onError,
}: UseDuplicateBriefOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const originalResponse = await ProjectBriefsService.getById({
        client: workspaceHttp,
        path: { platformProjectId: projectId, briefId },
      });
      const original = originalResponse.data;
      if (!original) {
        throw new Error('Failed to load brief');
      }

      const sections: CreateBriefSectionDto[] = [...original.sections]
        .sort((a, b) => a.position - b.position)
        .map((section) => ({
          name: section.name,
          position: section.position,
          required: section.required,
          aiAllowed: section.aiAllowed,
          ...(section.content !== undefined ? { content: section.content } : {}),
          ...(section.audienceIds.length > 0 ? { audienceIds: section.audienceIds } : {}),
        }));

      const sources: CreateBriefSourceDto[] = original.sources.map((source) => ({
        sourceType: source.sourceType,
        sourceId: source.sourceId,
        ...(source.metadata !== undefined ? { metadata: source.metadata } : {}),
      }));

      const body: CreateBriefDto = {
        title: i18n.t('common:brief.mutations.titleCopy', { title: original.title }),
        ...(original.briefType !== undefined ? { briefType: original.briefType } : {}),
        ...(original.fields !== undefined ? { fields: original.fields } : {}),
        ...(original.values !== undefined ? { values: original.values } : {}),
        ...(original.summary != null ? { summary: original.summary } : {}),
        ...(sections.length > 0 ? { sections } : {}),
        ...(sources.length > 0 ? { sources } : {}),
      };

      const createdResponse = await ProjectBriefsService.create({
        client: workspaceHttp,
        path: { platformProjectId: projectId },
        body,
      });
      const created = createdResponse.data;
      if (!created) {
        throw new Error('Failed to duplicate brief');
      }
      return created;
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({
        queryKey: BRIEFS_QUERY_KEYS.lists(projectId),
      });
      // Consumer owns the feedback when a callback is supplied (e.g. the detail
      // page's toast-with-link); otherwise fall back to the default toast so
      // the row-menu call site is unchanged.
      if (onSuccess) {
        onSuccess(created);
      } else {
        enqueueSnackbar(i18n.t('common:brief.mutations.briefDuplicated'), { variant: 'success' });
      }
    },
    onError: (error: Error) => {
      if (onError) {
        onError(error);
      } else {
        enqueueSnackbar(i18n.t('common:brief.mutations.failedToDuplicateBrief'), {
          variant: 'error',
        });
      }
    },
  });
};

/**
 * Updates a SINGLE Trackers-board custom-column value on a brief.
 *
 * The backend PATCH does a shallow merge of `customColumnValues`, so we can
 * safely send only `{ [columnId]: value }` here without clobbering the other
 * columns. We optimistically write the new value into the
 * `allByWorkspace(workspaceId)` cache so the cell updates instantly with no
 * server round-trip flicker.
 */
/**
 * Optimistically patch a single brief's custom-column value in the
 * `allByWorkspace` cache, matching by the globally-unique brief id across ALL
 * project groups.
 *
 * It deliberately does NOT gate on `group.projectId === writeProjectId`: on
 * workspaces with duplicate/aliased projects, the brief's write-target
 * projectId can differ from the projectId the group was fetched under. The old
 * projectId-gated matcher then skipped every group — a silent no-op — so the
 * edit persisted server-side (PATCH 200) but the cell only refreshed on a
 * manual reload. Matching by brief id is immune to that mismatch (and updates
 * every cached copy if a duplicate ever surfaces the same brief twice).
 *
 * `value === null` clears the key rather than storing null.
 */
function patchBriefValueInGroups(
  groups: ProjectBriefsGroup[],
  briefId: string,
  columnId: string,
  value: unknown,
): ProjectBriefsGroup[] {
  return groups.map((g) => {
    if (!g.briefs.some((b) => b.id === briefId)) return g;
    return {
      ...g,
      briefs: g.briefs.map((b) => {
        if (b.id !== briefId) return b;
        const base: Record<string, unknown> = { ...(b.customColumnValues ?? {}) };
        // Rebuild without the key rather than `delete` (no-dynamic-delete).
        const next =
          value === null
            ? Object.fromEntries(Object.entries(base).filter(([key]) => key !== columnId))
            : { ...base, [columnId]: value };
        return { ...b, customColumnValues: next };
      }),
    };
  });
}

export interface UseUpdateBriefCustomColumnValueOptions {
  workspaceId: string;
  projectId: string;
  briefId: string;
  /** When true, re-fetch briefs shortly after the edit so async Trackers
   *  automation results surface without a manual refresh. Pass the board's
   *  "has enabled automations" flag. */
  refetchForAutomations?: boolean;
}

export interface UpdateBriefCustomColumnValueVariables {
  columnId: string;
  /** `null` deletes the value for this column on this brief. */
  value: unknown;
}

export const useUpdateBriefCustomColumnValueMutation = ({
  workspaceId,
  projectId,
  briefId,
  refetchForAutomations,
}: UseUpdateBriefCustomColumnValueOptions) => {
  const queryClient = useQueryClient();

  return useMutation<BriefResponseDto, Error, UpdateBriefCustomColumnValueVariables>({
    mutationFn: async ({ columnId, value }) => {
      const response = await ProjectBriefsService.update({
        client: workspaceHttp,
        path: { platformProjectId: projectId, briefId },
        body: { customColumnValues: { [columnId]: value } } satisfies UpdateBriefDto,
      });
      if (!response.data) {
        throw new Error('Failed to update brief custom column value');
      }
      return response.data;
    },
    // Optimistic: patch the allByWorkspace cache before the network round-trip
    // returns. On error we rely on a refetch via invalidation (below).
    onMutate: async ({ columnId, value }) => {
      const queryKey = BRIEFS_QUERY_KEYS.allByWorkspace(workspaceId);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData<ProjectBriefsGroup[] | undefined>(queryKey, (groups) =>
        groups ? patchBriefValueInGroups(groups, briefId, columnId, value) : groups,
      );
      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      // Roll back to the snapshot we took in onMutate.
      const ctx = context as { snapshot: unknown } | undefined;
      if (ctx?.snapshot !== undefined) {
        queryClient.setQueryData(BRIEFS_QUERY_KEYS.allByWorkspace(workspaceId), ctx.snapshot);
      }
    },
    // No invalidation on success normally — our optimistic write is the truth
    // and matches the server. The exception is automations: the worker writes
    // AFTER the response, so re-read just this brief a beat later and patch it.
    onSuccess: () => {
      scheduleAutomationRefresh(
        () => patchBriefFromServer(queryClient, workspaceId, projectId, briefId),
        refetchForAutomations,
      );
    },
  });
};

/**
 * Unbound variant of {@link useUpdateBriefCustomColumnValueMutation} for
 * call sites that act on MANY briefs (e.g. the Trackers Kanban view, where a
 * drag can land on any card). Identity moves from hook-construction time to
 * per-call variables; the optimistic `allByWorkspace` cache patch is the same.
 */
export interface UpdateAnyBriefCustomColumnValueVariables {
  projectId: string;
  briefId: string;
  columnId: string;
  /** `null` deletes the value for this column on this brief. */
  value: unknown;
}

export const useUpdateAnyBriefCustomColumnValueMutation = ({
  workspaceId,
  refetchForAutomations,
}: {
  workspaceId: string;
  /** See {@link UseUpdateBriefCustomColumnValueOptions.refetchForAutomations}. */
  refetchForAutomations?: boolean;
}) => {
  const queryClient = useQueryClient();

  return useMutation<BriefResponseDto, Error, UpdateAnyBriefCustomColumnValueVariables>({
    mutationFn: async ({ projectId, briefId, columnId, value }) => {
      const response = await ProjectBriefsService.update({
        client: workspaceHttp,
        path: { platformProjectId: projectId, briefId },
        body: { customColumnValues: { [columnId]: value } } satisfies UpdateBriefDto,
      });
      if (!response.data) {
        throw new Error('Failed to update brief custom column value');
      }
      return response.data;
    },
    onMutate: async ({ briefId, columnId, value }) => {
      const queryKey = BRIEFS_QUERY_KEYS.allByWorkspace(workspaceId);
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueryData(queryKey);
      queryClient.setQueryData<ProjectBriefsGroup[] | undefined>(queryKey, (groups) =>
        groups ? patchBriefValueInGroups(groups, briefId, columnId, value) : groups,
      );
      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      const ctx = context as { snapshot: unknown } | undefined;
      if (ctx?.snapshot !== undefined) {
        queryClient.setQueryData(BRIEFS_QUERY_KEYS.allByWorkspace(workspaceId), ctx.snapshot);
      }
    },
    onSuccess: (_data, { projectId, briefId }) => {
      scheduleAutomationRefresh(
        () => patchBriefFromServer(queryClient, workspaceId, projectId, briefId),
        refetchForAutomations,
      );
    },
  });
};
